import { useCallback, useMemo, useState } from 'react';
import { ActivityPlayerInfoData, TeamPlayerInfo } from '@/entities/TeamPlayerInfo';
import { PlayerRole } from '@/lib/types';

type SortablePlayer = TeamPlayerInfo | ActivityPlayerInfoData;
type SortDirection = 'asc' | 'desc';
type SortablePlayerKeys = keyof (TeamPlayerInfo & ActivityPlayerInfoData) | 'availability' | 'status' | 'ratingDoubles' | 'payment';

interface SortConfig {
	key: SortablePlayerKeys;
	direction: SortDirection;
}

const statusSortOrder: Record<PlayerRole, number> = {
	[PlayerRole.TEAM_LEAGUE_TEAM_CAPTAIN]: 1,
	[PlayerRole.TEAM_LEAGUE_TEAM_CAPTAIN_ASSISTANCE]: 2,
	[PlayerRole.TEAM_LEAGUE_TEAM_PLAYER]: 3
};

const valueExtractors: Partial<Record<SortablePlayerKeys, (player: SortablePlayer) => string | number>> = {
	fullname: (player) => (player as TeamPlayerInfo).fullname?.toLowerCase() || '',
	availability: (player) => (player as TeamPlayerInfo).availability?.toLowerCase() || '',
	status: (player) => statusSortOrder[(player as TeamPlayerInfo).status as PlayerRole] ?? 99,
	ratingDoubles: (player) => Number((player as TeamPlayerInfo).ratingDoubles ?? 0),
	payment: (player) => Number((player as TeamPlayerInfo).payment ?? 0)
};

function getValue(player: SortablePlayer, key: SortablePlayerKeys): string | number {
	const extractor = valueExtractors[key];
	return extractor ? extractor(player) : ((player as Record<string, any>)[key] ?? '');
}

function compareValues(a: string | number, b: string | number, direction: SortDirection): number {
	const multiplier = direction === 'asc' ? 1 : -1;

	if (typeof a === 'number' && typeof b === 'number') {
		return (a - b) * multiplier;
	}

	if (typeof a === 'string' && typeof b === 'string') {
		return a.localeCompare(b) * multiplier;
	}

	return 0;
}

export function useSortablePlayers(players: SortablePlayer[]) {
	const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

	const sortedPlayers = useMemo(() => {
		if (!sortConfig) return players;

		const { key, direction } = sortConfig;

		return [...players].sort((a, b) => {
			const aValue = getValue(a, key);
			const bValue = getValue(b, key);
			return compareValues(aValue, bValue, direction);
		});
	}, [players, sortConfig]);

	const requestSort = useCallback((key: SortablePlayerKeys) => {
		setSortConfig((prev) => {
			if (!prev || prev.key !== key) {
				return { key, direction: 'asc' };
			} else {
				return {
					key,
					direction: prev.direction === 'asc' ? 'desc' : 'asc'
				};
			}
		});
	}, []);

	return {
		sortedPlayers,
		sortConfig,
		requestSort
	};
}
